/**
 * Copyright (C) 2020 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Coord } from '@turf/helpers';
import { coordAll } from '@turf/meta';
import { Feature, FeatureCollection } from '@turf/helpers';
import isNil from 'lodash/isNil';
import size from 'lodash/size';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FlightPathWaypoint, TerminalDetails } from '../../../shared/entities/terminal';
import { RootState } from '../../../store';
import { setFTVShouldZoomToFlightPath } from '../../../store/ui/actions';
import { getOtherTerminalOnAircraft } from '../../../utils/TerminalUtilities';
import { TAIL_TAG_HEIGHT } from '../../common/map/BaseMapWrapper';
import { GOOGLE_MAP_INSTANCE_KEY } from '../../common/map/GoogleMapSingleton';
import { BeamOnMap } from '../../../store/maps/reducer';
import { PortalsEnum } from '../../../shared/entities/portal';
export const FTV_MAP_ZOOM_BUFFER = TAIL_TAG_HEIGHT;
const FTV_NO_FLIGHT_PATH_ZOOM_BUFFER = 0.5; // buffer around the lat/lng of the terminal

interface FullTailViewMapContainerProps extends StateProps, DispatchProps {
  map: google.maps.Map;
}

// Pans and zooms the map in order to show the entire flight path and terminal
// with enough padding to show the tail tag
function panAndZoomToFlightPathAndTerminal(
  selectedTerminalFlightPath: FlightPathWaypoint[] | undefined,
  map: google.maps.Map,
  fullTailViewTerminal: TerminalDetails | undefined,
  beams: FeatureCollection,
) {
  let mapBounds = new google.maps.LatLngBounds();

  if (selectedTerminalFlightPath) {
    selectedTerminalFlightPath.forEach((flightPathWaypoint: FlightPathWaypoint) => {
      if (!isNil(flightPathWaypoint.latitudeDeg) && !isNil(flightPathWaypoint.longitudeDeg)) {
        mapBounds.extend({ lat: flightPathWaypoint.latitudeDeg, lng: flightPathWaypoint.longitudeDeg });
      }
    });
  }

  // ensure terminal lat lng are included in the bounds if available
  if (fullTailViewTerminal) {
    let terminalToUse = fullTailViewTerminal;
    const otherTerminalOnAircraft = getOtherTerminalOnAircraft(fullTailViewTerminal);
    if (
      otherTerminalOnAircraft.sourceTime > fullTailViewTerminal.sourceTime ||
      isNil(fullTailViewTerminal.latitudeDeg) ||
      isNil(fullTailViewTerminal.longitudeDeg)
    ) {
      terminalToUse = otherTerminalOnAircraft;
    }
    const { latitudeDeg, longitudeDeg } = terminalToUse;
    if (!isNil(latitudeDeg) && !isNil(longitudeDeg)) {
      mapBounds.extend({ lat: latitudeDeg, lng: longitudeDeg });
    }
  }

  if (!mapBounds.isEmpty()) {
    // Don't zoom in too far on only one marker
    if (mapBounds.getNorthEast().equals(mapBounds.getSouthWest())) {
      let extendPoint1 = new google.maps.LatLng(
        mapBounds.getNorthEast().lat() + FTV_NO_FLIGHT_PATH_ZOOM_BUFFER,
        mapBounds.getNorthEast().lng() + FTV_NO_FLIGHT_PATH_ZOOM_BUFFER,
      );
      let extendPoint2 = new google.maps.LatLng(
        mapBounds.getNorthEast().lat() - FTV_NO_FLIGHT_PATH_ZOOM_BUFFER,
        mapBounds.getNorthEast().lng() - FTV_NO_FLIGHT_PATH_ZOOM_BUFFER,
      );
      mapBounds.extend(extendPoint1);
      mapBounds.extend(extendPoint2);
    }

    map.fitBounds(mapBounds, 0);
  } else if (!isNil(beams) && size(beams)) {
    beams.features.forEach((beam: Feature) => {
      if (beam) {
        // if no flight path or terminal location then zoom to the beam if provided
        // @ts-ignore
        coordAll(beam).forEach((coordinate: Coord) => {
          mapBounds.extend(new google.maps.LatLng(coordinate[1], coordinate[0]));
        });
      }
    });

    if (!mapBounds.isEmpty()) {
      // pan and zoom to the beam/s without any padding
      map.fitBounds(mapBounds, 0);
    }
  }
}

/**
 * The container that imperatively controls the zoom of the FTV. This container does not
 * render any components.
 * The cases where the FTV will zoom are:
 *   - the pan/zoom button are clicked, which sets the shouldZoomToFlightPath to true
 *   - when any terminal details and flight path requests finish from the service
 */
export const FullTailViewMapZoomContainer: React.FC<FullTailViewMapContainerProps> = ({
  selectedTerminalFlightPath,
  map,
  fullTailViewTerminal,
  isRequestingTailDetails,
  isRequestingFlightPath,
  isRequestingBeams,
  setFTVShouldZoomToFlightPath,
  shouldZoomToFlightPath,
  currentBeamNames,
  beams,
}: FullTailViewMapContainerProps): null => {
  const { datapathUuid } = useParams();
  const [requestsFinished, setRequestsFinished] = useState(false);
  const [requestsStarted, setRequestsStarted] = useState(false);
  const [flightpathRequestStarted, setFlightpathRequestStarted] = useState(false);
  const [terminalRequestStarted, setTerminalRequestStarted] = useState(false);

  const panAndZoom = useCallback(() => {
    const currentBeams: FeatureCollection = {
      ...beams,
      features: beams.features.filter((feature: Feature) =>
        currentBeamNames.find(
          (currentBeam: BeamOnMap) =>
            feature.properties?.name === currentBeam.beamId &&
            feature.properties?.network === currentBeam.networkType &&
            feature.properties?.mapType === currentBeam.mapType,
        ),
      ),
    };

    panAndZoomToFlightPathAndTerminal(selectedTerminalFlightPath, map, fullTailViewTerminal, currentBeams);
  }, [selectedTerminalFlightPath, map, fullTailViewTerminal, currentBeamNames, beams]);

  // Ensure that on mount or terminal change the FTV should zoom to flight path
  useEffect(() => {
    setRequestsStarted(false);
    setFlightpathRequestStarted(false);
    setTerminalRequestStarted(false);
    setRequestsFinished(false);
    setFTVShouldZoomToFlightPath(true);
  }, [datapathUuid, setFTVShouldZoomToFlightPath]);

  // determine when requests have started:
  // wait until both the terminal details
  // and historical requests have started
  useEffect(() => {
    if (isRequestingFlightPath) {
      setFlightpathRequestStarted(true);
    }
    if (isRequestingTailDetails) {
      setTerminalRequestStarted(true);
    }
    if (flightpathRequestStarted && terminalRequestStarted) {
      setRequestsStarted(true);
    }
  }, [
    isRequestingFlightPath,
    isRequestingTailDetails,
    flightpathRequestStarted,
    terminalRequestStarted,
    setFlightpathRequestStarted,
    setTerminalRequestStarted,
    setRequestsStarted,
  ]);

  // determine when the flight path and terminal requests are finished
  // this is needed because the zoom should not occur until after this completes
  useEffect(() => {
    if (requestsStarted && !isRequestingFlightPath && !isRequestingTailDetails && !isRequestingBeams) {
      setRequestsFinished(true);
    }
  }, [isRequestingFlightPath, isRequestingTailDetails, isRequestingBeams, requestsStarted, setRequestsFinished]);

  // zoom to flight path and terminal
  useEffect(() => {
    if (shouldZoomToFlightPath && requestsFinished) {
      panAndZoom();
      setFTVShouldZoomToFlightPath(false);
    }
  }, [shouldZoomToFlightPath, requestsFinished, setFTVShouldZoomToFlightPath, panAndZoom]);

  return null;
};

const mapStateToProps = (state: RootState) => ({
  fullTailViewTerminal: state.terminals.tailDetailsPanelTerminal,
  selectedTerminalFlightPath: state.terminals.selectedTerminalFlightPath[PortalsEnum.UNIFIED_PORTAL],
  isRequestingTailDetails: state.terminals.isRequestingTailDetailsPanelTerminal,
  isRequestingFlightPath: state.terminalHistory.isRequestingTerminalHistory,
  isRequestingBeams: state.beams.isRequestingBeams,
  shouldZoomToFlightPath: state.ui.ftvShouldZoomToFlightPath,
  currentBeamNames:
    !isNil(state.maps.mapContext[GOOGLE_MAP_INSTANCE_KEY]?.overlaysUiState[state.maps.overlaysCurrentContext]) &&
    !isNil(
      state.maps.mapContext[GOOGLE_MAP_INSTANCE_KEY]?.overlaysUiState[state.maps.overlaysCurrentContext].currentBeams,
    )
      ? (state.maps.mapContext[GOOGLE_MAP_INSTANCE_KEY]?.overlaysUiState[state.maps.overlaysCurrentContext]
          .currentBeams as BeamOnMap[])
      : [],
  beams: state.beams.beams,
});
type StateProps = ReturnType<typeof mapStateToProps>;

const mapDispatchToProps = {
  setFTVShouldZoomToFlightPath,
};
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(FullTailViewMapZoomContainer);
